import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Brush, Palette, Eye, Sparkles, Scissors, PenTool, Star, Instagram } from 'lucide-react';

const MakeupArtistry: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Vogue Academy";
    
    return (
        <div className="min-h-screen bg-white font-serif text-black selection:bg-black selection:text-white border-[1px] border-black m-4 overflow-hidden relative">
            
            {/* Top Bar - Magazine Header */}
            <div className="border-b border-black py-4 px-8 flex justify-between items-center bg-white sticky top-0 z-50">
                <div className="font-sans text-[10px] font-bold uppercase tracking-[0.3em]">
                    Est. 2024 • Issue 01
                </div>
                <div className="hidden md:flex gap-8 font-sans text-xs font-bold uppercase tracking-widest">
                    <a href="#" className="hover:line-through decoration-1 decoration-black decoration-wavy">Masterclass</a>
                    <a href="#" className="hover:line-through decoration-1 decoration-black decoration-wavy">Portfolio</a>
                    <a href="#" className="hover:line-through decoration-1 decoration-black decoration-wavy">Agency</a>
                </div>
                <Button variant="outline" className="rounded-none border-black hover:bg-black hover:text-white font-sans text-xs uppercase tracking-widest px-6 h-8">
                    Apply
                </Button>
            </div>

            {/* Title / Name */}
            <header className="py-20 px-8 text-center border-b border-black">
                <h1 className="text-6xl md:text-9xl font-normal tracking-tight uppercase leading-[0.8]">
                    {schoolName.split(' ')[0]} <br/>
                    <span className="font-sans font-black italic text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-red-500">
                        {schoolName.split(' ').slice(1).join(' ') || "ARTISTRY"}
                    </span>
                </h1>
                <p className="font-sans text-xs font-bold uppercase tracking-[0.4em] mt-8">
                    The Authority in Beauty Education
                </p>
            </header>

            {/* Editorial Grid */}
            <section className="grid grid-cols-1 lg:grid-cols-3 min-h-[80vh]">
                 {/* Col 1: Text */}
                 <div className="p-12 border-b lg:border-b-0 lg:border-r border-black flex flex-col justify-between">
                     <div>
                         <span className="font-sans text-[10px] uppercase tracking-widest border border-black rounded-full px-2 py-1 mb-8 inline-block">Editor's Pick</span>
                         <h2 className="text-4xl font-normal mb-8 leading-tight">
                             Defining <br/>
                             the Face of <br/>
                             Tomorrow.
                         </h2>
                         <p className="font-sans text-sm leading-loose text-gray-500 max-w-sm mb-12">
                             We don't just teach application; we teach vision. From runway avant-garde to bridal elegance, master the techniques that define the industry.
                         </p>
                     </div>
                     
                     <div className="grid grid-cols-2 gap-4">
                         <div className="aspect-square bg-gray-100 flex items-center justify-center border border-black hover:invert transition-all cursor-pointer">
                             <Brush className="w-8 h-8" strokeWidth={1} />
                         </div>
                         <div className="aspect-square bg-gray-100 flex items-center justify-center border border-black hover:invert transition-all cursor-pointer">
                             <Palette className="w-8 h-8" strokeWidth={1} />
                         </div>
                     </div>
                 </div>

                 {/* Col 2: Image */}
                 <div className="lg:border-r border-black relative group overflow-hidden bg-black">
                     <img 
                        src="https://images.unsplash.com/photo-1512496015851-a90fb38ba796?q=80&w=1935&auto=format&fit=crop" 
                        alt="Macro Eye Makeup" 
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-60 transition-opacity duration-700"
                     />
                     <div className="absolute bottom-8 left-8 text-white mix-blend-difference">
                         <h3 className="text-6xl font-normal leading-none mb-2">01</h3>
                         <span className="font-sans text-xs uppercase tracking-widest font-bold">Bridal / Editorial / SFX</span>
                     </div>
                 </div>

                 {/* Col 3: List */}
                 <div className="flex flex-col">
                     {[
                         { title: "Color Theory", icon: Palette },
                         { title: "Skin Science", icon: Sparkles },
                         { title: "Brow Shaping", icon: Eye },
                         { title: "Kit Hygiene", icon: Scissors }, // Using scissors as tool icon placeholder
                     ].map((item, i) => (
                         <div key={i} className="flex-1 border-b border-black p-8 flex items-center justify-between group cursor-pointer hover:bg-black hover:text-white transition-colors last:border-b-0">
                             <div className="flex items-center gap-4">
                                 <span className="font-sans text-xs font-bold">0{i+1}</span>
                                 <span className="text-2xl italic font-serif">{item.title}</span>
                             </div>
                             <item.icon strokeWidth={1} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                         </div>
                     ))}
                 </div>
            </section>

            {/* Testimonial / Quote */}
            <div className="border-t border-black p-12 lg:p-24 text-center bg-[#f5f5f5]">
                <Star size={24} className="mx-auto mb-8 fill-black" />
                <blockquote className="text-3xl md:text-5xl font-serif max-w-4xl mx-auto leading-tight mb-8">
                    "Beauty is not just about how you look. It is about how you live."
                </blockquote>
                <cite className="font-sans text-xs font-bold uppercase tracking-widest not-italic">— Course Director</cite>
            </div>

             {/* Footer */}
            <footer className="bg-black text-white py-12 px-8 border-t border-black">
                 <div className="flex flex-col md:flex-row justify-between items-center">
                     <div className="mb-8 md:mb-0 text-center md:text-left">
                         <h2 className="text-2xl font-serif italic mb-2">{schoolName}</h2>
                         <p className="font-sans text-[10px] uppercase tracking-widest text-gray-400">
                             London • Paris • New York • Tokyo
                         </p>
                     </div>
                     
                     <div className="flex gap-6">
                          <Button variant="ghost" size="icon" className="hover:bg-white hover:text-black rounded-full">
                              <Instagram size={20} />
                          </Button>
                          <Button className="rounded-none bg-white text-black hover:bg-gray-200 font-sans text-xs font-bold uppercase tracking-widest px-8">
                              Get Prospectus
                          </Button>
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default MakeupArtistry;
